import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchHolterEvents,
  applyGroupEventChange,
  calculateStartStopStripMomentOfListEvent,
  calculateStartStopStripMomentOfListEventV2,
  handleAddEventOthersV2,
  formatEventStrip,
  updateEventChangeAndNew,
} from '@/entities/event';
import {
  selectActiveTab,
  selectAiChannel,
  selectChartHrsData,
  selectEcgObject,
  selectIsActiveTab,
  selectIsLoadingHrChart,
  selectIsLoadingHrChartOptions,
  selectJumpPortion,
  selectJumpTime,
  selectPickDateAction,
  selectPickedEndDate,
  selectPickedStartDate,
  selectPreventReloadEcg,
  selectBeatChanges,
  selectEventChanges,
  selectEventNews,
  selectEventOthers,
  selectReloadEcgViewer,
  selectNextColumnInfo,
  selectPrevColumnInfo,
  selectSelectedColumnInfo,
  selectSelectedEventIndex,
  selectSelectedEvent,
  selectSelectedStrip,
  selectGroupEventChanges,
  useReportStore,
} from '@/entities/report';
import { AIECGViewer, CAPTURE_MODE, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  ID_BOOKMARK_TAB,
  KEY_RECOIL,
  LIST_EVENT_TYPE_QUERY,
  OTHER_EVENT_LIST,
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_EVENT_OTHER,
  TYPE_RHYTHM_EVENT_ENUM,
  typesPersistEvent,
} from '@/shared/config';
import { convertToMomentJs, eventTypeConvert } from '@/shared/lib/aiUtils';
import { useDeepCompareEffect, useEmitter, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError, toastrSuccess } from '@/shared/lib/toastNotification';
import {
  checkAndProcessBeatData,
  countBeatChannels,
  generateUpdatedBeats,
  getUpdatedBeatMarker,
  handleUpdateBeatChanges,
  updateListImagesChangeV2,
} from '@/entities/beat';
import { checkAndProcessEcgData, formatDrawDisplay, formatDrawMutation } from '@/entities/ecg';
import { logError } from '@/shared/lib/apiHelpers';
import { isRangesOverlap } from '@/shared/lib/timeRange';
import { TPickDateAction } from '@/shared/model';
import type { BookmarkEcgViewerWrapperProps } from './bookmarkEcgViewerWrapper';

/**
 * Drives the ECG waveform viewer on the bookmark tab, downloading the strip around the selected minute and keeping its beats, event markers and bookmark indicators in step with unsaved edits. Wires tab-2 report-store reads to refs, rebuilds the viewer object on beat, event, bookmark and tab-activation changes, computes the date-picker and bookmark indicator positions, and handles strip capture, beat updates and Escape to cancel date picking.
 *
 * @param props - The wrapper inputs: study and profile ids, the ECG data map, timezone offset and the arrow-button callback.
 * @returns An object exposing loading flags, the ECG object, chart and strip refs, date-picking state, navigation disable flags, the selected event, indicator positions and the capture, arrow and beat-update handlers.
 */
const useBookmarkEcgViewerWrapper = ({
  ecgDataMap,
  studyId,
  profileId,
  timezoneOffset,
  handleClickArrowButton,
}: Pick<
  BookmarkEcgViewerWrapperProps,
  'ecgDataMap' | 'studyId' | 'profileId' | 'timezoneOffset' | 'handleClickArrowButton'
>) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const isLoadingHrChartOptions = useReportStore(selectIsLoadingHrChartOptions(keyRecoil));
  /**
   * Reads the unsaved edits to existing ECG events for the bookmark tab off the live store snapshot. Applies the tab-2 keyed event-changes selector and widens the result to a loose array.
   *
   * @returns The list of edited events for the bookmark tab.
   */
  const getEventChanges = (() =>
    selectEventChanges(keyRecoil)(useReportStore.getState())) as unknown as () => any[];
  /**
   * Reads the newly created, not yet persisted events for the bookmark tab off the store snapshot. Applies the tab-2 keyed new-events selector to the current report store state.
   *
   * @returns The list of newly added events for the bookmark tab.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  /**
   * Reads the pending beat marker edits for the bookmark tab straight off the report store rather than through a subscription. Applies the tab-2 keyed beat-changes selector to the current store snapshot.
   *
   * @returns The list of pending beat changes for the bookmark tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Stores the pending beat marker edits for the bookmark tab. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  const pickDateAction = useReportStore(selectPickDateAction(keyRecoil));
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Stores which end of the date range the bookmark tab is currently picking. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Stores the start timestamp picked on the bookmark ECG viewer. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Stores the end timestamp picked on the bookmark ECG viewer. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const selectedEventIndex = useReportStore(selectSelectedEventIndex(keyRecoil));
  const prevColumnInfo = useReportStore(selectPrevColumnInfo(keyRecoil));
  const nextColumnInfo = useReportStore(selectNextColumnInfo(keyRecoil));
  const aiChannel = useReportStore(selectAiChannel(keyRecoil));
  const setAiChannelAction = useReportStore((s) => s.actions.setAiChannel);
  /**
   * Records which ECG channel the AI beat detection favours on the bookmark tab. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setAiChannel = (v: Parameters<typeof setAiChannelAction>[1]) =>
    setAiChannelAction(keyRecoil, v);
  const jumpTime = useReportStore(selectJumpTime(keyRecoil));
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Stores the epoch the bookmark ECG viewer should scroll to. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const ecgObject = useReportStore(selectEcgObject(keyRecoil));
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Stores the assembled ECG viewer object for the bookmark tab, which is what makes the waveform re-render. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value.
   * @returns Nothing.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  const reloadEcgViewer = useReportStore(selectReloadEcgViewer(keyRecoil));
  /**
   * Reads the minute-by-minute heart-rate chart points for the bookmark tab without subscribing to updates. Applies the tab-2 keyed chart heart-rate selector to the current report store snapshot.
   *
   * @returns The heart-rate chart data points for the bookmark tab.
   */
  const getChartHrsData = () => selectChartHrsData(keyRecoil)(useReportStore.getState());
  /**
   * Reads the other-type captured events for the bookmark tab off the live store snapshot. Applies the tab-2 keyed other-events selector and widens the result to a loose array.
   *
   * @returns The list of other events for the bookmark tab.
   */
  const getEventOthers = (() =>
    selectEventOthers(keyRecoil)(useReportStore.getState())) as unknown as () => any[];
  const eventOthers = useReportStore(selectEventOthers(keyRecoil));
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Stores the other-type captured events for the bookmark tab. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  /**
   * Reads the window fraction the ECG viewer should place a jump target at on the bookmark tab. Applies the tab-2 keyed jump-portion selector to the current report store state.
   *
   * @returns The jump portion used when positioning the ECG window.
   */
  const getJumpPortion = () => selectJumpPortion(keyRecoil)(useReportStore.getState());
  /**
   * Reads the pending bulk event-type changes for the bookmark tab off the store snapshot. Applies the tab-2 keyed group-event-changes selector to the current report store state.
   *
   * @returns The list of grouped event changes for the bookmark tab.
   */
  const getGroupEventChanges = () => selectGroupEventChanges(keyRecoil)(useReportStore.getState());
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  const ecgBookmarkDeleteIds = useReportStore((s) => s.ecgBookmarkDeleteIds);
  const setSelectedEventInfoAction = useReportStore((s) => s.actions.setSelectedEventInfo);
  /**
   * Stores the events and beats making up the column selected on the bookmark tab. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedEventInfo = (v: Parameters<typeof setSelectedEventInfoAction>[1]) =>
    setSelectedEventInfoAction(keyRecoil, v);
  const setMinuteInfoEventsAction = useReportStore((s) => s.actions.setMinuteInfoEvents);
  /**
   * Stores the minute-info other events overlaid on the bookmark strip. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setMinuteInfoEvents = (v: Parameters<typeof setMinuteInfoEventsAction>[1]) =>
    setMinuteInfoEventsAction(keyRecoil, v);
  const setIsLoadingEcgViewerAction = useReportStore((s) => s.actions.setIsLoadingEcgViewer);
  /**
   * Flags whether the bookmark tab's ECG viewer is still loading. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingEcgViewer = (v: Parameters<typeof setIsLoadingEcgViewerAction>[1]) =>
    setIsLoadingEcgViewerAction(keyRecoil, v);
  const selectedEvent = useReportStore(selectSelectedEvent(keyRecoil)) as any;
  /**
   * Reads the currently selected ECG event for the bookmark tab off the live store snapshot. Applies the tab-2 keyed selected-event selector and widens the result to a loose type.
   *
   * @returns The selected event for the bookmark tab.
   */
  const getSelectedEvent = () =>
    selectSelectedEvent(keyRecoil)(useReportStore.getState()) as unknown as any;
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Stores the newly created, not yet persisted ECG events for the bookmark tab. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Stores the unsaved edits to existing ECG events for the bookmark tab. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  /**
   * Reads the flag that suppresses the next ECG reload for the bookmark tab off the store snapshot. Applies the tab-2 keyed prevent-reload selector to the current report store state.
   *
   * @returns True when the next ECG download should be skipped.
   */
  const getPreventReloadEcg = () => selectPreventReloadEcg(keyRecoil)(useReportStore.getState());
  const setPreventReloadEcgAction = useReportStore((s) => s.actions.setPreventReloadEcg);
  /**
   * Flags that the next ECG download for the bookmark tab should be skipped. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPreventReloadEcg = (v: Parameters<typeof setPreventReloadEcgAction>[1]) =>
    setPreventReloadEcgAction(keyRecoil, v);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  /**
   * Reads the currently selected strip or bookmark for the bookmark tab off the live store snapshot. Applies the tab-2 keyed selected-strip selector and widens the result to a loose type.
   *
   * @returns The selected strip for the bookmark tab.
   */
  const getSelectedStrip = () =>
    selectSelectedStrip(keyRecoil)(useReportStore.getState()) as unknown as any;
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Stores the event undo/redo snapshot options for the bookmark tab. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Stores the beat undo/redo snapshot options for the bookmark tab. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);

  const [isLoading, setIsLoading] = useState(false);
  const [isDisablePrevBtn, setIsDisablePrevBtn] = useState(false);
  const [isDisableNextBtn, setIsDisableNextBtn] = useState(false);

  const ecgChartRef = useRef<AIECGViewer>(null);
  const startStripEpoch = useRef(null);
  const stopStripEpoch = useRef(null);
  const originalBeatMarkers = useRef([]);
  const originalHesBeatStatus = useRef([]);
  const beatMarkersInEcgViewer = useRef([]);
  const hesBeatStatusInEcgViewer = useRef([]);
  const isJumpToEnd = useRef(false);
  const isJumpToStart = useRef(false);
  const currentQueryEventHour = useRef(null);
  const preventReloadEcgViewer = useRef(false);
  const keepEcgPosition = useRef(false);
  const savedEvents = useRef([]);
  const ecgObjectRef = useRef({});
  const previousBookmarksId = useRef(selectedStrip?.id);
  const keepBookmarkPositionRef = useRef(false);
  const currentBeatsChangeRef = useRef<any>();

  //* prevent api race condition by not setState data of old selected strip or event
  let isActiveQuery: boolean;
  const isPickingDate =
    pickDateAction === TPickDateAction.START || pickDateAction === TPickDateAction.END;

  /**
   * Converts one ECG event into the sample-offset marker the waveform viewer draws over the current strip. Colours artifact grey, afib orange and everything else red, clamps the event start and stop epochs into the strip window and scales them by sampling frequency, labels only the red events, and picks a jump portion of 2 for beat events or 10 otherwise.
   *
   * @param event - The event or minute-info record to place; records without start and stop yield zero positions.
   * @param isSelected - Whether this event is the one currently selected in the viewer.
   * @param selectedStrip - The strip whose idEvent decides which marker is highlighted.
   * @returns A marker descriptor carrying type, startPos, stopPos, color, id, isSelected, portion and isHighLight.
   */
  const getEventInfoInEcgViewer = ({ event, isSelected = false, selectedStrip }: any) => {
    const { samplingFrequency } = ecgDataMap;
    const isEvent = event.start && event.stop; // not minute info (minute info dont have start and stop)
    let startPos = 0;
    let stopPos = 0;
    let color;
    if (isEvent) {
      const isArtifactEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
      const isAfibEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.AFIB;
      color = isArtifactEvent ? 'gray' : isAfibEvent ? 'orange' : 'red';
      const startEpoch = new Date(event.start).getTime();
      const stopEpoch = new Date(event.stop).getTime();
      if (startStripEpoch.current <= startEpoch) {
        const eventDiffSeconds = Math.abs(startEpoch - startStripEpoch.current) / 1000;
        startPos = Math.round(eventDiffSeconds * samplingFrequency);
      }
      if (stopEpoch >= stopStripEpoch.current) {
        stopPos = (stopStripEpoch.current - startStripEpoch.current) / 1000;
      } else {
        stopPos = (stopEpoch - startStripEpoch.current) / 1000;
      }
      stopPos = Math.round(stopPos * samplingFrequency);
    }
    return {
      type: isEvent && color === 'red' ? (eventTypeConvert as any)[event.type] : '', // Show event type text in ecg viewer for others event
      startPos,
      stopPos,
      color,
      id: event.id,
      isSelected,
      portion: (TYPE_BEAT_EVENT_ENUM as any)[event.type] ? 2 : 10, // Determine start window when jump to
      isHighLight: selectedStrip?.idEvent === event.idEvent,
    };
  };

  const createObjectEcg = ({
    ecg,
    beats,
    hesBeatStatus,
    events,
    eventIndex,
    exportEcgData,
  }: any) => {
    const { channels, gain, samplingFrequency } = ecgDataMap;
    // *: Add capture sample mark
    let drawStartSample = 0;
    const eventSelected = getSelectedEvent();
    const findEvent = _.find(events, (x) => x.idEvent === eventSelected?.idEvent);
    let drawList = [];
    if (findEvent?.type) {
      drawList = findEvent?.draw;
    } else {
      const otherEvents = getEventOthers() as any[];
      const filterRemoveOther = _.filter(otherEvents, (x) => !x.isRemovedStrip);
      drawList =
        _.filter(_.flatten(_.map(filterRemoveOther, (event) => event.draw)), (x) => !!x) || [];
    }
    const { draw, startSample } = formatDrawDisplay(
      drawList,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );
    const eventsInfo = _.map(events, (event, index) =>
      getEventInfoInEcgViewer({
        event,
        isSelected: index === eventIndex,
        selectedStrip: findEvent,
      }),
    );
    savedEvents.current = _.cloneDeep(events);
    if (startSample) {
      drawStartSample = startSample;
    }
    const viewerObject = {
      options: {},
      widgets: draw,
    };
    const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeats(
      beats,
      hesBeatStatus,
      getBeatChanges(),
      startStripEpoch.current,
      stopStripEpoch.current,
    );
    beatMarkersInEcgViewer.current = updatedBeats;
    hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
    const endStripPos =
      ((stopStripEpoch.current - startStripEpoch.current) / 1000) * samplingFrequency;
    const selectedStrip = getSelectedStrip();

    const jumpPos = selectedStrip?.isBookmarkStrip
      ? Math.round(
          ((selectedStrip.center - 5000 - startStripEpoch.current) / 1000) *
            ecgDataMap.samplingFrequency,
        )
      : 0;

    const newEcgObject = {
      dataSamples: ecg,
      adcGain: gain,
      adcZero: 0,
      numChannels: channels.length,
      viewerObject,
      diagnosticLead: 2,
      samplingRate: samplingFrequency,
      events: eventsInfo,
      aiPredict: [
        {
          beats: updatedBeats,
          hesBeatStatus: updatedHesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
        },
      ],
      isAppliedAIByDefault: true,
      isShowVES: true,
      isShowSVES: true,
      isShowBeatMarkers: true,
      jumpOffset: keepBookmarkPositionRef.current
        ? jumpPos
        : keepEcgPosition.current
          ? ecgChartRef.current?.getOffsetX()
          : isJumpToStart.current
            ? 1
            : isJumpToEnd.current
              ? endStripPos
              : drawStartSample,
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
      exportEcgData,
    };
    setEcgObject(newEcgObject);
    keepEcgPosition.current = false;
    ecgObjectRef.current = newEcgObject;
  };

  /**
   * Downloads and assembles the ECG samples, beats and overlapping events for the minute selected on the bookmark tab. Bails out when a reload-suppression ref or store flag is set, fetches the hour's holter events plus ECG and beat data in parallel, merges unsaved event changes and neighbouring column events, trims samples and beats to the computed strip window, then builds the viewer object and toggles the prev and next buttons from the adjacent minutes' average heart rate.
   *
   * @param selectedEvents - The selected column's events, the last of which carries the minute start epoch.
   * @param eventIndex - Index within the events marking which one is drawn as selected.
   * @returns A promise resolving once the strip has been downloaded and the ECG viewer object rebuilt.
   */
  const downloadDataEcgV2 = async (selectedEvents: any[], eventIndex = 0) => {
    if (_.isEmpty(selectedEvents)) {
      return;
    }
    if (preventReloadEcgViewer.current) {
      preventReloadEcgViewer.current = false;
      return;
    }

    if (getPreventReloadEcg()) {
      setPreventReloadEcg(false);
      return;
    }
    setIsLoading(true);
    setIsLoadingEcgViewer(true);
    try {
      const events = [...selectedEvents];
      const minuteInfo = events[events.length - 1];
      const startHours = moment(minuteInfo.startTimeEpoch).startOf('hours');
      const { defaultStartStrip, defaultStopStrip } = calculateStartStopStripMomentOfListEventV2({
        startTimeEpoch: minuteInfo.startTimeEpoch,
      });
      const startSearchDate = startHours.toISOString();
      const stopSearchDate = startHours.clone().add(1, 'hours').toISOString();
      currentQueryEventHour.current = startSearchDate;

      const { events: downloadEvents } = await fetchHolterEvents(
        {
          studyId,
          profileId,
          types: LIST_EVENT_TYPE_QUERY,
          startSearchDate,
          stopSearchDate,
        },
        0,
        true,
        null,
        false,
      );

      const [ecgData, holterBeats] = await Promise.all([
        checkAndProcessEcgData({
          ecgDataMap,
          dataLength:
            ((defaultStopStrip.valueOf() - defaultStartStrip.valueOf()) / 1000) *
            ecgDataMap.samplingFrequency,
          // checkAndProcessEcgData's momentObject type (common/handlerEcgData.ts, Task 7) declares
          // string | number, but the function itself does moment(momentObject.startMoment) - it accepts Moment too.
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          } as any,
        }),
        checkAndProcessBeatData({
          ecgDataMap,
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          } as any,
        }),
      ]);
      if (isActiveQuery) {
        const startMinuteString = moment(minuteInfo.startTimeEpoch).toISOString();
        const stopMinuteString = moment(minuteInfo.startTimeEpoch).add(60, 'seconds').toISOString();
        const downloadEventsFormat = formatEventStrip(downloadEvents);
        const downloadEventsUpdate = updateEventChangeAndNew(
          downloadEventsFormat,
          getEventChanges(),
          getEventNews(),
          startSearchDate,
          stopSearchDate,
          typesPersistEvent,
          true,
        );
        applyGroupEventChange(downloadEventsUpdate, getGroupEventChanges());
        // isRangesOverlap (handler.ts, Task 7) declares number params but every call site here
        // passes ISO date strings, which compare correctly lexicographically - pre-existing.
        _.forEach(downloadEventsUpdate, (downloadEvent: any) => {
          if (
            isRangesOverlap(
              downloadEvent.start,
              downloadEvent.stop,
              startMinuteString as any,
              stopMinuteString as any,
            )
          ) {
            downloadEvent.startTimeEpoch = minuteInfo.startTimeEpoch;
            events.push(downloadEvent);
          }
        });
        const { startStrip, stopStrip } = calculateStartStopStripMomentOfListEvent({
          events,
        });
        const startTimeString = startStrip.toISOString();
        const stopTimeString = stopStrip.toISOString();
        _.forEach(nextColumnInfo, (event: any) => {
          if (
            !_.find(events, (x: any) => x.idEvent === event.idEvent) &&
            isRangesOverlap(event.start, event.stop, startTimeString as any, stopTimeString as any)
          ) {
            events.push(event);
          }
        });
        _.forEach(prevColumnInfo, (event: any) => {
          if (
            !_.find(events, (x: any) => x.idEvent === event.idEvent) &&
            isRangesOverlap(event.start, event.stop, startTimeString as any, stopTimeString as any)
          ) {
            events.push(event);
          }
        });

        _.remove(events, (x) => x.type === TYPE_BEAT_ENUM.DELETED);
        const selectedEventInfo = [...events];
        const sortedSelectedEventInfo = _.sortBy(selectedEventInfo, [
          (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.AFIB,
          (event) => !OTHER_EVENT_LIST.includes(event.type),
          (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT,
          (event) => event?.start,
        ]);

        setSelectedEventInfo(
          _.uniqBy(
            _.filter(sortedSelectedEventInfo, (x) => !x.isNew && x.type !== TYPE_EVENT_OTHER),
            'idEvent',
          ),
        );
        const otherEvents = _.filter(sortedSelectedEventInfo, (x) => x.type === TYPE_EVENT_OTHER);
        const minuteInfoEvs = _.uniqBy([...eventOthers, ...otherEvents], 'id');
        setMinuteInfoEvents(minuteInfoEvs);

        startStripEpoch.current = startStrip.valueOf();
        stopStripEpoch.current = stopStrip.valueOf();
        // * Cut ecgdata and beat in range
        const cuttedMsStart = startStripEpoch.current - defaultStartStrip.valueOf();
        const cuttedMsStop = defaultStopStrip.valueOf() - stopStripEpoch.current;
        const cuttedSampleStart = (cuttedMsStart / 1000) * ecgDataMap.samplingFrequency;
        const cuttedSampleStop = (cuttedMsStop / 1000) * ecgDataMap.samplingFrequency;
        const cuttedEcgData = _.map(ecgData as any[], (subArr: any) =>
          subArr.slice(cuttedSampleStart, subArr.length - cuttedSampleStop),
        );
        const cuttedBeatSample: any[] = [];
        const cuttedHesBeatStatus: any[] = [];
        const cuttedBeatChannel: any[] = [];
        if (!_.isEmpty(holterBeats?.beats)) {
          _.forEach(holterBeats.beats, (sample, index) => {
            const epochBeat =
              defaultStartStrip.valueOf() + (sample * 1000) / ecgDataMap.samplingFrequency;
            if (epochBeat >= startStripEpoch.current && epochBeat <= stopStripEpoch.current) {
              cuttedHesBeatStatus.push(holterBeats?.hesBeatStatus[index]);
              cuttedBeatChannel.push(holterBeats?.beatChannels[index]);
              cuttedBeatSample.push(
                Math.round(
                  ((epochBeat - startStripEpoch.current) / 1000) * ecgDataMap.samplingFrequency,
                ),
              );
            }
          });
        }
        const cuttedHolterBeats = {
          beats: cuttedBeatSample,
          hesBeatStatus: cuttedHesBeatStatus,
          beatChannels: cuttedBeatChannel,
        };
        setAiChannel(countBeatChannels(cuttedHolterBeats?.beatChannels) as any);
        const eventsInfo = _.filter(events, (x: any) => x.type !== TYPE_EVENT_OTHER);
        const exportStart = startStripEpoch.current - 10000;
        const exportStop = stopStripEpoch.current + 10000;
        const ecgExport = await checkAndProcessEcgData({
          ecgDataMap,
          dataLength: ((exportStart - exportStop) / 1000) * ecgDataMap.samplingFrequency,
          momentObject: {
            startMoment: moment(exportStart),
            stopMoment: moment(exportStop),
          } as any,
        });
        createObjectEcg({
          ecg: cuttedEcgData,
          beats: cuttedHolterBeats?.beats,
          hesBeatStatus: cuttedHolterBeats?.hesBeatStatus,
          events: _.uniqWith(eventsInfo, (a: any, b: any) => {
            const aCopy = { id: a?.id, type: a?.type, idEvent: a?.idEvent };
            const bCopy = { id: b?.id, type: b?.type, idEvent: b?.idEvent };
            return _.isEqual(aCopy, bCopy);
          }),
          eventIndex,
          exportEcgData: {
            ecgData: ecgExport,
          },
        });
        originalBeatMarkers.current = cuttedHolterBeats?.beats;
        originalHesBeatStatus.current = cuttedHolterBeats?.hesBeatStatus;
        const hrsData = getChartHrsData();
        const prevMinute = minuteInfo.startTimeEpoch - 60000;
        const nextMinute = minuteInfo.startTimeEpoch + 60000;
        const prevMinuteHrData = _.find(hrsData, (x) => x.id === prevMinute);
        const nextMinuteHrData = _.find(hrsData, (x) => x.id === nextMinute);
        setIsDisablePrevBtn(!prevMinuteHrData?.avgHr);
        setIsDisableNextBtn(!nextMinuteHrData?.avgHr);
        setIsLoading(false);
      }
      setIsLoadingEcgViewer(false);
      isJumpToEnd.current = false;
      isJumpToStart.current = false;
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError('Failed to download ECG data: ', error);
      }
      setIsLoading(false);
      setIsLoadingEcgViewer(false);
    }
  };

  /**
   * Rebuilds the bookmark ECG viewer object from the original beat markers so the strip redraws with current settings once the tab goes inactive. Regenerates beats and HES statuses against the pending beat changes, clones the existing ECG object, and reassigns the hidden channels, filter, speed, amplitude and window settings from the viewer config store.
   *
   * @returns Nothing.
   */
  const reCreateObjectEcg = () => {
    if (!_.isEmpty(ecgObject)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeats(
        originalBeatMarkers.current,
        originalHesBeatStatus.current,
        getBeatChanges(),
        startStripEpoch.current,
        stopStripEpoch.current,
      );
      beatMarkersInEcgViewer.current = updatedBeats;
      hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
      const clonedEcgObject = _.cloneDeep(ecgObject);
      _.assign(clonedEcgObject, {
        hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
        hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
        notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
        speed: ECG_VIEWER_CONFIG_STORE.spdValue,
        amp: ECG_VIEWER_CONFIG_STORE.ampValue,
        wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
        aiPredict: [
          {
            beats: updatedBeats,
            hesBeatStatus: updatedHesBeatStatus,
            channel: 1,
            hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
          },
        ],
      });
      setEcgObject(clonedEcgObject);
      ecgObjectRef.current = clonedEcgObject;
    }
  };

  /**
   * Turns a click offset inside the bookmark ECG strip into an ISO timestamp for the date range being picked. Adds the offset in seconds to the strip start epoch in the study timezone and writes the result to either the picked start or the picked end date depending on the active pick action, doing nothing while no picking is in progress.
   *
   * @param diffSeconds - Seconds from the strip start to the clicked position.
   * @returns Nothing.
   */
  const handlePickDate = (diffSeconds: number) => {
    if (isPickingDate) {
      const date = convertToMomentJs(startStripEpoch.current, timezoneOffset)
        .add(diffSeconds, 'seconds')
        .toISOString();
      if (pickDateAction === TPickDateAction.START) {
        setPickedStartDate(date);
      } else {
        setPickedEndDate(date);
      }
    }
  };

  /**
   * Refreshes the bookmark ECG viewer's event markers and capture widgets after a strip capture or a change of selected event. Skips when there are no saved events or no viewer object, recomputes marker positions for every saved event, rebuilds the draw widgets from the selected event's changes or from the other events, regenerates beats, and reassigns everything onto a clone of the ECG object.
   *
   * @param config - Options carrying isUpdateEvents and isCapture, which control the two early-exit guards.
   * @returns Nothing.
   */
  const updateEcgCapture = (config: any) => {
    const { isUpdateEvents = false, isCapture = false } = config || {};
    if (isUpdateEvents && _.isEmpty(savedEvents.current)) {
      return;
    }
    if (_.isEmpty(ecgObjectRef.current) && !isCapture) return;
    const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
    const selectedStrip = getSelectedStrip();
    const selectedEv = getSelectedEvent();
    const findEvent = _.find(savedEvents.current, (x) => x?.idEvent === selectedEv?.idEvent);
    const eventsInfo = _.map(savedEvents.current, (event) =>
      getEventInfoInEcgViewer({
        event,
        isSelected: findEvent?.idEvent === event?.idEvent,
        selectedStrip: findEvent,
      }),
    );

    const eventChanges = getEventChanges();
    const selectedEvent = getSelectedEvent();
    const selectedEventIndex = _.findIndex(
      eventChanges,
      (x) => x.idEvent === selectedEvent.idEvent,
    );
    const selectedEventChanges =
      selectedEventIndex > -1 ? eventChanges[selectedEventIndex] : selectedEvent;
    let drawList = [];
    if (selectedEventChanges?.type) {
      drawList = selectedEventChanges?.draw;
    } else {
      const otherEvents = getEventOthers();
      const filterRemoveOther = _.filter(otherEvents, (x) => !x.isRemovedStrip);
      drawList =
        _.filter(_.flatten(_.map(filterRemoveOther, (event) => event.draw)), (x) => !!x) || [];
    }
    const { samplingFrequency } = ecgDataMap;
    const { draw } = formatDrawDisplay(
      drawList,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );
    const jumpPos = selectedStrip?.isBookmarkStrip
      ? Math.round(
          ((selectedStrip.center - 5000 - startStripEpoch.current) / 1000) *
            ecgDataMap.samplingFrequency,
        )
      : 0;
    const viewerObject = {
      options: {},
      widgets: draw,
    };

    const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeats(
      originalBeatMarkers.current,
      originalHesBeatStatus.current,
      getBeatChanges(),
      startStripEpoch.current,
      stopStripEpoch.current,
    );
    beatMarkersInEcgViewer.current = updatedBeats;
    hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;

    _.assign(clonedEcgObject, {
      viewerObject,
      jumpOffset: keepBookmarkPositionRef.current ? jumpPos : ecgChartRef.current?.getOffsetX(),
      events: eventsInfo,
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
      aiPredict: [
        {
          beats: updatedBeats,
          hesBeatStatus: updatedHesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
        },
      ],
    });

    setEcgObject(clonedEcgObject);
    ecgObjectRef.current = clonedEcgObject;
  };

  useUpdateEffect(() => {
    if (selectedStrip?.id && selectedStrip?.id !== previousBookmarksId.current) {
      keepBookmarkPositionRef.current = true;

      previousBookmarksId.current = selectedStrip.id;
    }
    // activeTabState also allows TBeatHrActiveTab/TYPE_RHYTHM_EVENT_ENUM/TYPE_BEAT_EVENT_ENUM, none of which include ID_BOOKMARK_TAB's literal - atom-type gap.
    if ((activeButton as any) !== ID_BOOKMARK_TAB) {
      previousBookmarksId.current = undefined;
    }
  }, [selectedStrip, isActiveTab, activeButton]);
  useEffect(() => {
    updateEcgCapture({ isUpdateEvents: true });
  }, [selectedEvent.idEvent, isActiveTab]);
  useEmitter(EMITTER_CONSTANTS.UPDATE_CAPTURE_EVENT, updateEcgCapture, [ecgObject]);

  const handleCaptureImages = ({
    draw,
    allIndexStripSvg,
    technicianComments,
    captureMode,
    captureTitle,
    visibleChannels,
  }: any) => {
    try {
      if (allIndexStripSvg.length > 0) {
        if (captureMode === CAPTURE_MODE.EVENT) {
          if (selectedEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
            keepEcgPosition.current = true;
          } else {
            preventReloadEcgViewer.current = true;
          }
          const { samplingFrequency } = ecgDataMap;
          const { formatDraw } = formatDrawMutation(
            draw,
            samplingFrequency,
            startStripEpoch.current,
            timezoneOffset,
            visibleChannels,
          );
          if (selectedEvent.isNew) {
            const data = updateListImagesChangeV2(
              getEventNews(),
              selectedEvent,
              formatDraw,
              allIndexStripSvg,
              technicianComments,
            );
            // Perform grouping event action applicable to undo/redo
            setEventOptions((prevOptions: any) => ({
              ...prevOptions,
              eventNewsState: data,
            }));
            setEventNews(data);
          } else {
            const data = updateListImagesChangeV2(
              getEventChanges(),
              selectedEvent,
              formatDraw,
              allIndexStripSvg,
              technicianComments,
            );
            // Perform grouping event action applicable to undo/redo
            setEventOptions((prevOptions: any) => ({
              ...prevOptions,
              eventChangesState: data,
            }));
            setEventChanges(data);
          }
          toastrSuccess(
            'You can find captured strip in "Strips Management" tab.',
            'Strip captured',
          );
          updateEcgCapture({ isCapture: true });
        }
        if (captureMode === CAPTURE_MODE.OTHER) {
          const { samplingFrequency } = ecgDataMap;
          const { formatDraw, stripStart, stripStop } = formatDrawMutation(
            draw,
            samplingFrequency,
            startStripEpoch.current,
            timezoneOffset,
            visibleChannels,
          );
          const startTime = stripStart;
          const endTime = stripStop;
          // handleAddEventOthersV2 (BeatHR/helper, Task 8) also declares a required miniteInfo field this call site doesn't pass - pre-existing.
          const data = handleAddEventOthersV2({
            events: getEventOthers(),
            draw: formatDraw,
            allIndexStripSvg,
            technicianComments,
            captureTitle,
            startTime,
            endTime,
          } as any);
          setEventOptions((prevOptions: any) => ({
            ...prevOptions,
            eventOthersState: data,
          }));
          setEventOthers(data);
        }
      }
    } catch (error) {
      logError('Failed to capture images: ', error);
      toastrError(error.message, 'Error');
    }
  };

  /**
   * Records beat marker edits made directly in the bookmark ECG viewer as pending beat changes. Diffs the incoming markers and HES statuses against the ones currently drawn, falling back to the originals, merges the diff into the existing beat changes through a functional store update, then mirrors the result into the beat undo/redo options and the drawn-marker refs.
   *
   * @param updatedBeatObject - The viewer payload carrying currentBeatMarkers and currentHesBeatStatus after the edit.
   * @returns Nothing.
   */
  const handleUpdateBeat = (updatedBeatObject: any) => {
    const { currentBeatMarkers, currentHesBeatStatus } = updatedBeatObject;
    const updatedBeatMarkers = getUpdatedBeatMarker(
      currentBeatMarkers,
      _.isEmpty(beatMarkersInEcgViewer.current)
        ? originalBeatMarkers.current
        : beatMarkersInEcgViewer.current,
      currentHesBeatStatus,
      _.isEmpty(hesBeatStatusInEcgViewer.current)
        ? originalHesBeatStatus.current
        : hesBeatStatusInEcgViewer.current,
      originalHesBeatStatus.current,
      startStripEpoch.current,
      ecgDataMap.samplingFrequency,
    );
    let beatsChanges: any[] = [];

    setBeatChanges((currBeatChange: any) => {
      const updatedBeatChanges = handleUpdateBeatChanges(updatedBeatMarkers, currBeatChange);
      currentBeatsChangeRef.current = updatedBeatChanges;
      beatsChanges = updatedBeatChanges;
      return updatedBeatChanges;
    });
    setBeatOptions((prev: any) => ({ ...prev, beatChangesState: beatsChanges }));
    beatMarkersInEcgViewer.current = currentBeatMarkers;
    hesBeatStatusInEcgViewer.current = currentHesBeatStatus;
  };

  /**
   * Steps the bookmark ECG viewer to the neighbouring minute and records which edge of the new strip the waveform should open at. Sets the jump-to-start ref when moving forward and the jump-to-end ref otherwise, then delegates to the handleClickArrowButton prop.
   *
   * @param isNext - True to move to the following minute, false to move back.
   * @returns Nothing.
   */
  const clickArrowButtonHandle = useCallback((isNext: boolean) => {
    if (isNext) {
      isJumpToStart.current = true;
    } else {
      isJumpToEnd.current = true;
    }
    handleClickArrowButton(isNext);
  }, []);

  /**
   * Decides which capture mode the strip capture dialog should open in for the currently selected event. Returns the artifact mode for artifact and deleted-beat events, the event mode for any other typed event, and the other mode when nothing is selected.
   *
   * @returns One of the capture mode constants.
   */
  const getCaptureMode = () => {
    if (selectedEvent?.type) {
      if (
        selectedEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ||
        selectedEvent.type === TYPE_BEAT_ENUM.DELETED
      ) {
        return CAPTURE_MODE.ARTIFACT;
      }
      return CAPTURE_MODE.EVENT;
    }
    return CAPTURE_MODE.OTHER;
  };

  useDeepCompareEffect(
    () => {
      isActiveQuery = true;
      if (isRenderViewerEcg) {
        downloadDataEcgV2(selectedColumnInfo, selectedEventIndex);
      }
    },
    [selectedColumnInfo, eventChanges, reloadEcgViewer, isRenderViewerEcg],
    () => {
      isActiveQuery = false;
    },
  );

  /**
   * Redraws the bookmark ECG viewer in place while preserving the current horizontal scroll offset. Regenerates beats from the originals plus pending changes, rebuilds the capture widgets from the selected event's draw list or from the other events, reassigns the viewer config settings onto a clone of the ECG object, and resets the chart's interaction mode first when beats changed.
   *
   * @param isChangeBeats - When true, resets the chart's interaction mode before applying the redraw.
   * @returns Nothing.
   */
  const reDrawEcgObjectKeepPosition = (isChangeBeats?: boolean) => {
    if (!_.isEmpty(ecgObject)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeats(
        originalBeatMarkers.current,
        originalHesBeatStatus.current,
        getBeatChanges(),
        startStripEpoch.current,
        stopStripEpoch.current,
      );
      beatMarkersInEcgViewer.current = updatedBeats;
      hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
      const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
      const { samplingFrequency } = ecgDataMap;
      const eventChanges = getEventChanges();
      const selectedEvent = getSelectedEvent();
      const selectedEventIndex = _.findIndex(
        eventChanges,
        (x) => x.idEvent === selectedEvent.idEvent,
      );
      const selectedEventChanges =
        selectedEventIndex > -1 ? eventChanges[selectedEventIndex] : selectedEvent;
      const otherEvents = getEventOthers();
      const drawList = selectedEventChanges?.type
        ? selectedEventChanges?.draw
        : _.filter(_.flatten(_.map(otherEvents, (event) => event.draw)), (x) => !!x) || [];
      const { draw } = formatDrawDisplay(
        drawList,
        startStripEpoch.current,
        samplingFrequency,
        stopStripEpoch.current,
      );
      const viewerObject = {
        options: {},
        widgets: draw,
      };

      _.assign(clonedEcgObject, {
        viewerObject,
        jumpOffset: ecgChartRef.current?.getOffsetX(),
        hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
        hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
        notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
        speed: ECG_VIEWER_CONFIG_STORE.spdValue,
        amp: ECG_VIEWER_CONFIG_STORE.ampValue,
        wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
        aiPredict: [
          {
            beats: updatedBeats,
            hesBeatStatus: updatedHesBeatStatus,
            channel: 1,
            hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
          },
        ],
      });
      if (isChangeBeats) {
        if (ecgChartRef.current) {
          ecgChartRef.current.resetInMode(true);
        }
      }
      setEcgObject(clonedEcgObject);
      ecgObjectRef.current = clonedEcgObject;
    }
  };

  // Re-create ECG object when beat changes
  useUpdateEffect(() => {
    if (isRenderViewerEcg) {
      reDrawEcgObjectKeepPosition();
    }
  }, [ecgBookmarks, eventOthers, isRenderViewerEcg]);

  useUpdateEffect(() => {
    if (!_.isEqual(beatChanges, currentBeatsChangeRef.current)) {
      currentBeatsChangeRef.current = beatChanges;
      reDrawEcgObjectKeepPosition(true);
    }
  }, [beatChanges]);

  useUpdateEffect(() => {
    if (!isActiveTab) {
      reCreateObjectEcg();
    } else if (ecgChartRef.current) {
      // resetInMode's real signature requires an arg; this pre-existing call site omits it.
      (ecgChartRef.current as any).resetInMode();
    }
  }, [isActiveTab]);

  useUpdateEffect(() => {
    /**
     * Scrolls the bookmark ECG viewer to the requested jump timestamp once the chart has finished loading. Converts the jump epoch into a sample position from the strip start and sampling frequency, calls the chart's jump with the stored portion and clears the jump time, retrying every 10ms while the chart has not loaded, and does nothing when the target falls outside the strip.
     *
     * @returns Nothing.
     */
    const handleJumpToEcgViewer = () => {
      if (jumpTime <= stopStripEpoch.current && jumpTime >= startStripEpoch.current) {
        if (ecgChartRef.current?.hasLoadChart()) {
          const jumpPos = Math.round(
            (((jumpTime as any) - startStripEpoch.current) / 1000) * ecgDataMap.samplingFrequency,
          );
          const jumpPortion = getJumpPortion();
          ecgChartRef.current.handleJumpTo(jumpPos, jumpPortion);
          setJumpTime(undefined);
          // setJumpPortion(0);
        } else {
          setTimeout(() => {
            handleJumpToEcgViewer();
          }, 10);
        }
      }
    };
    if (
      jumpTime &&
      !_.isEmpty(ecgChartRef.current) &&
      startStripEpoch.current &&
      stopStripEpoch.current &&
      !isLoading
    ) {
      handleJumpToEcgViewer();
    }
  }, [
    jumpTime,
    isLoading,
    ecgObject, // trigger useEffect when data has cached
  ]);

  const eventIndicator = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    let startPos = null;
    let endPos = null;
    if (pickedStartDate) {
      const pickedStartEpoch = moment(pickedStartDate).valueOf();
      startPos = Math.round(
        ((pickedStartEpoch - startStripEpoch.current) / 1000) * samplingFrequency,
      );
    }
    if (pickedEndDate) {
      const pickedEndEpoch = moment(pickedEndDate).valueOf();
      endPos = Math.round(((pickedEndEpoch - startStripEpoch.current) / 1000) * samplingFrequency);
    }
    return {
      startPos,
      endPos,
    };
  }, [pickedStartDate, pickedEndDate, ecgObject]);

  const bookmarksInRange = useMemo(() => {
    const result: any[] = [];
    _.forEach(ecgBookmarks, (bookmark: any) => {
      if (
        bookmark.center >= startStripEpoch.current &&
        bookmark.center <= stopStripEpoch.current &&
        !ecgBookmarkDeleteIds.includes(bookmark.id)
      ) {
        result.push(bookmark);
      }
    });
    return result;
  }, [ecgBookmarks, ecgBookmarkDeleteIds, ecgObject]);

  const bookmarkIndicators = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    const listBookmarks: any[] = [];
    _.forEach(bookmarksInRange, (bookmark: any) => {
      listBookmarks.push({
        sample: Math.round(
          ((bookmark.center - startStripEpoch.current) / 1000) * samplingFrequency,
        ),
        id: bookmark.center,
      });
    });
    return listBookmarks;
  }, [bookmarksInRange, ecgObject]);

  useEffect(() => {
    /**
     * Cancels an in-progress date pick on the bookmark ECG strip when Escape is released. Ignores auto-repeat key events and resets the pick action to none only while a pick action is active.
     *
     * @param event - The keyboard event delivered by the window keyup listener.
     * @returns Nothing.
     */
    const handleKeyup = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const { key } = event;
      if (key === 'Escape' && pickDateAction) {
        setPickDateAction(TPickDateAction.NONE);
      }
    };
    if (isPickingDate) {
      window.addEventListener('keyup', handleKeyup);
    }
    return () => {
      window.removeEventListener('keyup', handleKeyup);
    };
  }, [pickDateAction, isPickingDate]);

  return {
    isLoading,
    isLoadingHrChart,
    isLoadingHrChartOptions,
    ecgObject,
    startStripEpoch,
    ecgChartRef,
    pickDateAction,
    isPickingDate,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    selectedEvent,
    eventIndicator,
    bookmarkIndicators,
    handlePickDate,
    getCaptureMode,
    handleCaptureImages,
    clickArrowButtonHandle,
    handleUpdateBeat,
  };
};

export default useBookmarkEcgViewerWrapper;
